import React, { useMemo } from 'react';
import { Modal } from '@vimily/bonjoro-ui-components-v2/Modal';
import cx from 'classnames';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { UpgradeModalTrigger } from './UpgradeModalFlow/util';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import useCurrentTeam from '~/react-hooks/use-current-team';
import { openShopifyCheckoutPage } from '~/App/Modules/NewBilling/utils';
import { BillingCycle } from '~/types/models';
import { ShopifyVideoMessagingPlanSlugs } from '~/types/plans';

export interface ShopifyUpgradeModalProps {
  trigger: UpgradeModalTrigger;
  open?: boolean;
  onCloseClick?: () => void;
}

const RightComponentContainer = styled.div`
  background: transparent linear-gradient(180deg, #3358ff 0%, #0001b3 100%) 0%
    0% no-repeat padding-box;
`;

const features: { feature: string; description: string }[] = [
  {
    feature: 'Roll-ups',
    description:
      'send a message to 250 contacts at a time. Great for new product launches, personalised updates to special customers and empty cart follow-up.',
  },
  {
    feature: 'Get your team involved',
    description:
      'bring 1-2 more team members in and get them sending with you.',
  },
  {
    feature: 'Remove Bonjoro branding',
    description:
      'on the Growth plan, you can also remove all Bonjoro branding so its 100% yours.',
  },
  {
    feature: 'Custom Domains',
    description: 'All your videos will be hosted on video.yourdomain.com',
  },
];

export function ShopifyUpgradeModal({
  open,
  onCloseClick,
  trigger,
}: ShopifyUpgradeModalProps) {
  const { billing_cycle: teamBillingCycle } = useCurrentTeam();

  const planSlug = useMemo(() => {
    if (teamBillingCycle == BillingCycle.Monthly) {
      return ShopifyVideoMessagingPlanSlugs.GrrrowthMonthly;
    }

    return ShopifyVideoMessagingPlanSlugs.GrrrowthYearly;
  }, [teamBillingCycle]);

  const upgradeText = useMemo(() => {
    switch (trigger) {
      case UpgradeModalTrigger.TeamMemberLimit:
        return 'Upgrade today to invite additional team members';
      case UpgradeModalTrigger.CustomDomain:
        return 'Upgrade today to get Custom Domain';
      default:
        return 'Upgrade today to get more features';
    }
  }, [trigger]);

  const upgrade = (
    <div className='py-3 px-5 flex gap-4 v2-bg-yellow-100 v2-text-neutral-900 text-base font-bold'>
      <img
        src='https://cdn.bonjoro.com/assets/Flat-icons/crown.svg'
        className='w-[30px] h-5'
      />
      <div>{upgradeText}</div>
    </div>
  );

  return (
    <Modal
      open={open}
      onModalClose={onCloseClick}
      closeButtonClassName='!top-6 !right-6 text-[30px] !w-5'
      className={cx(
        'w-full !inset-px !p-0 max-w-[1030px] overflow-hidden rounded-[20px]'
      )}
    >
      <div className='flex flex-col lg:flex-row '>
        <div className='flex flex-col flex-1 w-full shrink-0'>
          {upgrade}
          <div className='flex flex-col text-neutral-900 text-base gap-y-5 px-5 py-4'>
            <div className='font-bold text-[28px] leading-8'>
              Get the most out of Bonjoro as a Shopify user
            </div>
            <div>
              Our Shopify customers, like Tyler over there -&gt;, get the most
              out of the platform using our Grrrowth plan.
            </div>
            {features.map((feature, i) => {
              return (
                <div className='flex gap-3.5' key={i}>
                  <FontAwesomeIcon icon={faCircleCheck} className='text-xl' />
                  <div>
                    <b>{feature.feature}</b>: {feature.description}
                  </div>
                </div>
              );
            })}

            <Button
              variant={ButtonVariant.Primary}
              className='v2-text-neutral-900 w-full !py-2'
              onClick={() => openShopifyCheckoutPage(planSlug)}
            >
              Upgrade now
            </Button>
            <div className='v2-text-neutral-500 text-xs self-center'>
              Cancel or downgrade anytime, no questions asked
            </div>
          </div>
        </div>
        <RightComponentContainer
          className={cx([
            'flex flex-col justify-center align-center w-full text-center',
            'lg:w-2/4 px-5 pt-14 bg-cover bg-no-repeat bg-center rounded-r gap-y-4',
          ])}
        >
          <div className='flex flex-col text-white items-center pt-12 gap-5'>
            <i
              className={cx(
                'icon icon-sm icon-quote w-[50px] h-[38px] v2-bg-yellow-500'
              )}
            />
            <div className='text-[21px] font-bold'>
              We&apos;ve seen over 25% more repeat sales and 100s of 5-star
              reviews. Bonjoro is unique and I&apos;d happily recommend it to
              any eCommerce seller.
            </div>
            <div className='text-base'>Tyler McCombs - CEO of Devon & Lang</div>
            <img
              src='https://cdn.bonjoro.com/assets/images/tyler-hp.png'
              className='w-[387px] h-[306px]'
            />
          </div>
        </RightComponentContainer>
      </div>
    </Modal>
  );
}
